'use client';

import type { ReactNode } from 'react';

import type { BaseComponentProps } from '../../base-component.types';
import { CloseButton, ResourceBar } from '../../resource';
import type { CloseButtonProps } from '../../resource';
import type { InstructionsType } from '../Question.types';
import { BookmarkButton, InstructionsTooltip } from './QuestionControls';
import { TimeLimit } from './TimeLimit';

type QuestionToolbarProps = BaseComponentProps & {
  /** The whole seconds left; nothing at all puts no clock on the bar. */
  secondsLeft?: number;
  bookmarked?: boolean;
  onBookmark?: () => void;
  instructions?: InstructionsType;
  /** Controls at the leading edge, beside the bookmark. */
  options?: ReactNode;
  /** Controls at the trailing edge, before the close button. */
  actions?: ReactNode;
  /** How leaving is offered. Left out, the bar carries no way out at all. */
  close?: CloseButtonProps;
};

/**
 * The bar every question screen is topped with. It carries only what all of them
 * have — the bookmark, the clock and the way out — and takes the rest as slots, so a
 * screen composes the controls it wants instead of the bar growing a prop for each.
 */
function QuestionToolbar({
  style,
  className,
  secondsLeft,
  bookmarked,
  onBookmark,
  instructions,
  options,
  actions,
  close,
  testId,
}: QuestionToolbarProps) {
  return (
    <ResourceBar
      style={style}
      className={className}
      edge="top"
      testId={testId}
      start={
        <>
          <BookmarkButton bookmarked={bookmarked} onToggle={onBookmark} />
          {options}
        </>
      }
      center={<TimeLimit seconds={secondsLeft} showIcon={false} />}
      end={
        <>
          {instructions && <InstructionsTooltip instructions={instructions} />}
          {actions}
          {close && <CloseButton {...close} />}
        </>
      }
    />
  );
}

export { QuestionToolbar };
